import React, { useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Card from 'react-bootstrap/Card'
import Collapse from 'react-bootstrap/Collapse'
import Table from 'react-bootstrap/Table'
import SurvivorEditor from './SurvivorEditor'
import { findById as findStateById } from '../../../js/states'
import { changedFields } from './survivorDraft'

const SCORE_HELP =
  'How plausible the group is, 0-100: the name (max 50), how far apart they are ' +
  '(max 35) and layouts they share (max 15). A group scores what its weakest pair scores.'

function scoreVariant(score) {
  if (score >= 75) return 'success'
  if (score >= 55) return 'warning'
  return 'secondary'
}

// A course nobody ever placed on a map has no distance, which is not the same as
// being far away — saying "0 m" or "—" without a reason is how it went unnoticed
// that these courses were never proposed at all.
function distanceOf(member) {
  if (member.relation === 'SURVIVOR') return '—'
  if (member.metres == null) return <span className="text-muted small">no location</span>
  return `${member.metres} m`
}

function sharedLayoutsOf(member) {
  const shared = member.sharedLayouts
  if (!shared) return null
  const parts = []
  if (shared.identical > 0) parts.push(`${shared.identical} same layout`)
  if (shared.sameShape > 0) parts.push(`${shared.sameShape} same shape`)
  return parts.length > 0 ? parts.join(', ') : null
}

// The state a member sits in, by short code, or null in a country that has none. The full name is
// the tooltip: a column of "Georgia" / "Washington" is wider than the table can spare.
function stateOf(member) {
  return member.state ? findStateById(member.state) || null : null
}

// One proposed merge.
//
// Group-shaped rather than row-shaped, because a merge is one decision about
// several courses: the server rejects a group whole if any member has changed
// since it was proposed, since merging a subset of what somebody approved is not
// what they approved.
//
// Accept is pre-selected, matching the runner's prefilled action=merge — every
// proposed group is already believed, so the work is rejecting the ones you
// disagree with rather than confirming the ones you don't.
//
// The two states are two buttons rather than one toggle. A single button that
// reads "Will merge" is the state, not the action, and gets clicked by anyone who
// reads it as "click here to accept" — which silently rejects the group and, once
// every group is rejected, empties the apply panel and looks like a stuck page.
// Merge/Skip cannot be misread, and the pair matches the trash pass.
//
// The card can also apply itself. The batch panel at the foot of the page is still there and still
// the faster way through thirty easy groups, but a group you have just spent five minutes picking
// fields for should not have to wait for the other twenty-nine — and a batch is one irreversible
// click over work of very uneven confidence. Preview is required either way, per group.
export default function MergeGroupCard({
  group,
  accepted,
  onChange,
  draft,
  onDraftChange,
  onCombine,
  onReset,
  onPreview,
  onMerge,
  previewed,
  applying,
  // False where there is no batch to be in and no sibling groups to skip past: the
  // country page's admin panel, where ticking the courses *is* the accept. Skip there
  // would mean "untick what you just ticked", which the checkboxes already say.
  batched = true,
}) {
  const rows = [group.survivor, ...group.members]
  const [editing, setEditing] = useState(false)
  const changed = draft ? changedFields(group, draft) : []
  // Clustering is by name and distance, and a course with no location has no distance -- so a
  // group can pair "Central Park" in two states purely on its name. In a country with states,
  // show which one each member is in, and say it loudly when they are not all the same.
  const showState = rows.some((member) => member.state)
  const stateKeys = new Set(rows.map((member) => member.state || null))
  const mixedStates = showState && stateKeys.size > 1

  return (
    <Card className={`mb-3 curation-merge-group ${accepted ? '' : 'curation-merge-skipped'}`}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>
          <strong>{group.survivor.name}</strong>{' '}
          <span className="text-muted">survives · {group.members.length} absorbed</span>
          {!accepted && (
            <Badge bg="secondary" className="ms-2">
              skipped — will not be merged
            </Badge>
          )}
          {mixedStates && (
            <Badge
              bg="danger"
              className="ms-2"
              title="Not every course in this group is in the same state"
            >
              different states
            </Badge>
          )}
          {accepted && changed.length > 0 && (
            <Badge bg="primary" className="ms-2" title={changed.join(', ')}>
              survivor edited — {changed.length} fields
            </Badge>
          )}
        </span>
        <span className="d-flex align-items-center gap-2">
          {/* The number and the badge are the same judgement at two resolutions, so they
              come from one field on the wire and can never disagree. */}
          <Badge bg={scoreVariant(group.score)} title={SCORE_HELP}>
            {group.score} / 100
          </Badge>
          <Badge bg={group.confidence === 'LIKELY' ? 'success' : 'warning'}>
            {group.confidence}
          </Badge>
        </span>
      </Card.Header>

      <Table size="sm" responsive className="mb-0">
        <thead>
          <tr>
            <th>Id</th>
            <th>Name</th>
            {showState && <th>State</th>}
            <th>Relation</th>
            <th>Distance</th>
            <th>Score</th>
            <th>Layouts</th>
            <th>Played</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((member) => (
            <tr
              key={member.courseId}
              className={
                accepted && member.relation === 'SURVIVOR' ? 'table-success' : undefined
              }
            >
              <td>{member.courseId}</td>
              <td>
                {member.name}
                {member.secondaryName && (
                  <span className="text-muted"> · {member.secondaryName}</span>
                )}
              </td>
              {showState && (
                <td
                  className={mixedStates ? 'text-danger fw-semibold' : undefined}
                  title={stateOf(member)?.name}
                >
                  {stateOf(member)?.short ?? <span className="text-muted small">none</span>}
                </td>
              )}
              <td className="small">{member.relation}</td>
              <td>{distanceOf(member)}</td>
              <td>
                {member.score == null ? (
                  '—'
                ) : (
                  <>
                    <strong>{member.score}</strong>
                    {sharedLayoutsOf(member) && (
                      <span className="text-muted small"> · {sharedLayoutsOf(member)}</span>
                    )}
                  </>
                )}
              </td>
              <td>{member.layoutCount}</td>
              {/* Games follow layouts, so this is what the merge is actually moving. */}
              <td>{member.played > 0 ? <strong>{member.played}</strong> : 0}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {accepted && (
        <>
          <Card.Body className="py-2">
            <Button
              size="sm"
              variant="link"
              className="p-0"
              onClick={() => setEditing(!editing)}
              aria-expanded={editing}
            >
              {editing ? 'Hide' : 'Edit'} the surviving course
            </Button>
            {!editing && changed.length === 0 && (
              <span className="text-muted small ms-2">
                Untouched, so the merge decides its fields as it always has.
              </span>
            )}
          </Card.Body>
          <Collapse in={editing}>
            <div>
              <Card.Body className="pt-0">
                <SurvivorEditor
                  group={group}
                  draft={draft}
                  onChange={onDraftChange}
                  onCombine={onCombine}
                  onReset={onReset}
                />
              </Card.Body>
            </div>
          </Collapse>
        </>
      )}

      <Card.Footer className="d-flex align-items-center gap-3 flex-wrap">
        {batched && (
          <ButtonGroup size="sm">
            <Button
              variant={accepted ? 'success' : 'outline-success'}
              onClick={() => onChange(true)}
            >
              Merge
            </Button>
            <Button
              variant={accepted ? 'outline-secondary' : 'secondary'}
              onClick={() => onChange(false)}
            >
              Skip
            </Button>
          </ButtonGroup>
        )}

        {accepted && (
          <ButtonGroup size="sm">
            <Button variant="outline-primary" disabled={applying} onClick={onPreview}>
              Preview this group
            </Button>
            {/* Same rule as the batch panel: apply is dead until a dry run for *this* group has
                been read. A preview of the group next to it says nothing about this one. */}
            <Button
              variant="danger"
              disabled={applying || !previewed}
              onClick={onMerge}
              title={
                previewed
                  ? 'Merges this group now. This cannot be undone.'
                  : 'Read the preview first'
              }
            >
              Merge this group now — cannot be undone
            </Button>
          </ButtonGroup>
        )}

        <span className="text-muted small">
          {!accepted
            ? 'Left alone. Nothing about these courses changes.'
            : batched
              ? `Included in the batch: ${group.members.length} absorbed into #${group.survivor.courseId}.`
              : `${group.members.length} absorbed into #${group.survivor.courseId}.`}
        </span>
      </Card.Footer>
    </Card>
  )
}
